import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import { AcademicYearsFeature } from "../academicYears/academicYears.feature";
import type { AcademicYearDto } from "../academicYears/api/academicYears.api";
import { useAcademicYears } from "../academicYears/hooks/useAcademicYears";

import {
  readStoredAcademicYearSelection,
  writeStoredAcademicYearSelection,
} from "./academicYearSelectionStorage";

import { patchUser } from "@/auth/authSlice";
import { usePermission } from "@/auth/permissions/usePermission";
import { useAppSelector } from "@/redux/hooks";
import * as authService from "@/services/authService";

type AcademicYearContextValue = {
  academicYears: AcademicYearDto[];
  isLoadingAcademicYears: boolean;
  activeAcademicYearId: string | null;
  selectedAcademicYearId: string | null;
  canSelectPreviousYears: boolean;
  isViewingPreviousYear: boolean;
  academicYearMode: string | null;
  isReadOnlyMode: boolean;
  isSwitchingAcademicYear: boolean;
  selectAcademicYear: (academicYearId: string) => Promise<void>;
};

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getActiveAcademicYearIdStorageKey(tenantId: string) {
  return `sms_activeAcademicYearId_v1:${tenantId}`;
}

function getActiveAcademicYearStorageKey(tenantId: string) {
  return `sms_activeAcademicYear_v1:${tenantId}`;
}

const defaultContextValue: AcademicYearContextValue = {
  academicYears: [],
  isLoadingAcademicYears: true,
  activeAcademicYearId: null,
  selectedAcademicYearId: null,
  canSelectPreviousYears: false,
  isViewingPreviousYear: false,
  academicYearMode: null,
  isReadOnlyMode: false,
  isSwitchingAcademicYear: false,
  selectAcademicYear: async () => {},
};

const AcademicYearContext = createContext<AcademicYearContextValue>(defaultContextValue);

export function AcademicYearProvider({ children }: { children: React.ReactNode }) {
  const tenantId = useAppSelector((s) => s.auth.user?.tenantId) ?? null;
  const accessTokenFromStore = useAppSelector((s) => s.auth.user?.accessToken) ?? null;
  const { can } = usePermission();
  const canSelectPreviousYears = can(
    AcademicYearsFeature.permissions.viewPrevious ??
      AcademicYearsFeature.permissions.view,
  );
  const dispatch = useDispatch();
  const { data: academicYears = [], isLoading: isLoadingAcademicYears } =
    useAcademicYears();

  // Read cached active year (id + display info) from localStorage for instant render,
  // scoped to the current tenant. This is derived with useMemo (not useState+effect) so it
  // can never lag a tenant change by even one render — a stale useState seed here was
  // exactly what let a *previous* tenant's academic-year id survive long enough for
  // `sections`/`classes` (gated on activeAcademicYearId, keyed on
  // selectedAcademicYearId ?? activeAcademicYearId — see below) to fetch with it and 404.
  const activeAcademicYearSeed = useMemo(() => {
    if (!tenantId) return null;
    try {
      const raw = localStorage.getItem(getActiveAcademicYearStorageKey(tenantId));
      return raw
        ? (JSON.parse(raw) as { id: string; name: string; isActive: boolean })
        : null;
    } catch {
      return null;
    }
  }, [tenantId]);

  // NOTE: clearing the tenant-scoped React Query cache is deliberately NOT done here.
  // A provider effect cannot order itself before its own descendants' query subscriptions
  // (effects run child-first), so a `queryClient.clear()` in this position always fired
  // *after* the new tenant's /academic-years fetch had already started — and clear() cancels
  // in-flight fetches silently (queryCache.clear -> remove -> destroy -> cancel({silent:true})),
  // which notifies no observer, so nothing re-rendered and nothing retried. That race is what
  // left the selector stuck on "Select academic year".
  // Cache reset on tenant change is an *event*, not a derived value, so it lives in the code
  // that performs the switch: usePlatformTenantSession (start/end) and the forced-exit path in
  // services/axios.ts, where it runs synchronously before React re-renders.

  const activeAcademicYearIdFromApi = useMemo(() => {
    const active = academicYears.find((y) => y.isActive);
    return active?.id ?? null;
  }, [academicYears]);

  // Persist full active year object to localStorage whenever the API resolves.
  useEffect(() => {
    if (tenantId && activeAcademicYearIdFromApi && academicYears.length > 0) {
      const activeYear = academicYears.find((y) => y.id === activeAcademicYearIdFromApi);
      if (activeYear) {
        try {
          localStorage.setItem(
            getActiveAcademicYearIdStorageKey(tenantId),
            activeAcademicYearIdFromApi,
          );
          localStorage.setItem(
            getActiveAcademicYearStorageKey(tenantId),
            JSON.stringify({
              id: activeYear.id,
              name: activeYear.name,
              isActive: activeYear.isActive,
            }),
          );
        } catch {
          // ignore
        }
      }
    }
  }, [tenantId, activeAcademicYearIdFromApi, academicYears]);

  // Use the API list once it has resolved; otherwise fall back to the tenant-scoped
  // localStorage seed, so the selector shows the right label (and canWrite stays true)
  // immediately instead of flashing a placeholder / disabled button.
  //
  // The id and the option list MUST derive from the same predicate. They used to disagree:
  // `activeAcademicYearId` fell back to the seed unconditionally, while the option list
  // injected it only while `isLoadingAcademicYears`. In React Query v5 `isLoading` is
  // `isPending && isFetching`, so any settled-but-dataless state (a failed request, or a
  // cancelled one) yielded a non-null `activeAcademicYearId` alongside an *empty* option
  // list. That combination is the regression: AcademicYearSelect resolves its label via
  // `options.find(o => o.value === value)` and falls through to the "Select academic year"
  // placeholder, while sections/classes — gated on `Boolean(activeAcademicYearId)` — fetch
  // under the stale seed id. Deriving both from `hasApiAcademicYears` makes that state
  // unrepresentable: a non-null activeAcademicYearId always has a matching option.
  const hasApiAcademicYears = academicYears.length > 0;
  const usingSeededAcademicYear = !hasApiAcademicYears && activeAcademicYearSeed !== null;

  const activeAcademicYearId = hasApiAcademicYears
    ? activeAcademicYearIdFromApi
    : (activeAcademicYearSeed?.id ?? null);

  const effectiveAcademicYears = useMemo<AcademicYearDto[]>(() => {
    if (!usingSeededAcademicYear || !activeAcademicYearSeed) {
      return academicYears;
    }
    return [
      {
        id: activeAcademicYearSeed.id,
        name: activeAcademicYearSeed.name,
        isActive: activeAcademicYearSeed.isActive,
        startDate: "",
        endDate: "",
        createdAt: "",
      },
    ];
  }, [usingSeededAcademicYear, activeAcademicYearSeed, academicYears]);

  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string | null>(
    () => (tenantId ? readStoredAcademicYearSelection(tenantId) : null),
  );

  // Reset synchronously — during render, not in a useEffect — the instant the tenant
  // changes (React's documented pattern for resetting state when a dependency changes,
  // via https://react.dev/reference/react/useState#storing-information-from-previous-renders).
  // An effect-based reset only runs one render *after* tenantId already changed, and in
  // that render `effectiveAcademicYearId` in dependent queries (sections/classes: keyed on
  // selectedAcademicYearId ?? activeAcademicYearId, gated only on activeAcademicYearId) was
  // still resolving to the previous tenant's id — exactly the bug being fixed here.
  // Falling back to null (not the raw stored value for the new tenant) means dependent
  // queries fall back to activeAcademicYearId, which is itself null until
  // useAcademicYears' tenant-scoped query resolves — so nothing fires with any stale id.
  const selectedYearTenantIdRef = useRef(tenantId);
  if (selectedYearTenantIdRef.current !== tenantId) {
    selectedYearTenantIdRef.current = tenantId;
    if (selectedAcademicYearId !== null) {
      setSelectedAcademicYearId(null);
    }
  }

  const [isSwitchingAcademicYear, setIsSwitchingAcademicYear] = useState(false);

  const academicYearMode = useMemo(() => {
    const token = accessTokenFromStore || localStorage.getItem("accessToken") || "";
    const payload = token ? decodeJwtPayload(token) : null;
    const mode = payload?.AcademicYearMode ?? payload?.academicYearMode ?? null;
    return typeof mode === "string" && mode.trim() ? mode : null;
  }, [accessTokenFromStore]);

  const isReadOnlyMode = academicYearMode === "ReadOnly";

  // Initialize / reconcile selection when tenant, permissions, or years change.
  useEffect(() => {
    if (!tenantId) {
      setSelectedAcademicYearId(null);
      return;
    }

    if (!activeAcademicYearId) {
      // No active year yet; keep selection null.
      setSelectedAcademicYearId(null);
      return;
    }

    const stored = readStoredAcademicYearSelection(tenantId);
    const storedIsKnown = stored ? academicYears.some((y) => y.id === stored) : false;

    const storedAllowed =
      storedIsKnown && stored !== activeAcademicYearId && canSelectPreviousYears;

    const next = storedAllowed ? (stored as string) : activeAcademicYearId;

    setSelectedAcademicYearId((prev) => {
      if (prev === next) return prev;
      writeStoredAcademicYearSelection(tenantId, next);
      return next;
    });
  }, [tenantId, academicYears, activeAcademicYearId, canSelectPreviousYears]);

  const selectAcademicYear = useCallback(
    async (academicYearId: string) => {
      if (!tenantId) return;
      if (!academicYearId) return;
      if (!activeAcademicYearId) return;

      const exists = academicYears.some((y) => y.id === academicYearId);
      if (!exists) return;

      // Only allow selecting a non-active year if permission exists.
      const isNonActive = academicYearId !== activeAcademicYearId;
      if (isNonActive && !canSelectPreviousYears) {
        setSelectedAcademicYearId(activeAcademicYearId);
        writeStoredAcademicYearSelection(tenantId, activeAcademicYearId);
        return;
      }

      const previousSelection = selectedAcademicYearId;
      setIsSwitchingAcademicYear(true);
      try {
        const deviceId = authService.getDeviceIdentifier();
        const authResult = await authService.switchAcademicYear(academicYearId, deviceId);

        // Persist tokens for axios + refresh.
        localStorage.setItem("accessToken", authResult.accessToken);
        localStorage.setItem("refreshToken", authResult.refreshToken);
        localStorage.setItem(
          "tokenExpiry",
          authService.getTokenExpiration(authResult.expiresIn).toString(),
        );

        // Patch the persisted auth session (best-effort).
        try {
          const raw = localStorage.getItem("auth_session_v1");
          if (raw) {
            const session = JSON.parse(raw);
            const next = {
              ...session,
              user: {
                ...(session.user ?? {}),
                accessToken: authResult.accessToken,
                refreshToken: authResult.refreshToken,
              },
              tokenExpiry: authService
                .getTokenExpiration(authResult.expiresIn)
                .toString(),
            };
            localStorage.setItem("auth_session_v1", JSON.stringify(next));
          }
        } catch {
          // ignore
        }

        dispatch(
          patchUser({
            accessToken: authResult.accessToken,
            refreshToken: authResult.refreshToken,
            dataScope: authResult.dataScope,
            activeContext: authResult.activeContext,
          }),
        );
        setSelectedAcademicYearId(academicYearId);
        writeStoredAcademicYearSelection(tenantId, academicYearId);

        if (isNonActive) {
          toast.success("Switched to previous academic year (read-only)");
        } else {
          toast.success("Switched to active academic year");
        }
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : "Failed to switch academic year";
        toast.error(message);

        const fallback = previousSelection ?? activeAcademicYearId;
        setSelectedAcademicYearId(fallback);
        writeStoredAcademicYearSelection(tenantId, fallback);
      } finally {
        setIsSwitchingAcademicYear(false);
      }
    },
    [
      tenantId,
      academicYears,
      activeAcademicYearId,
      canSelectPreviousYears,
      dispatch,
      selectedAcademicYearId,
    ],
  );

  const isViewingPreviousYear = Boolean(
    activeAcademicYearId &&
    selectedAcademicYearId &&
    selectedAcademicYearId !== activeAcademicYearId,
  );

  const value = useMemo<AcademicYearContextValue>(
    () => ({
      academicYears: effectiveAcademicYears,
      isLoadingAcademicYears,
      activeAcademicYearId,
      selectedAcademicYearId,
      canSelectPreviousYears,
      isViewingPreviousYear,
      academicYearMode,
      isReadOnlyMode,
      isSwitchingAcademicYear,
      selectAcademicYear,
    }),
    [
      effectiveAcademicYears,
      isLoadingAcademicYears,
      activeAcademicYearId,
      selectedAcademicYearId,
      canSelectPreviousYears,
      isViewingPreviousYear,
      academicYearMode,
      isReadOnlyMode,
      isSwitchingAcademicYear,
      selectAcademicYear,
    ],
  );

  return (
    <AcademicYearContext.Provider value={value}>{children}</AcademicYearContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAcademicYearContext(): AcademicYearContextValue {
  return useContext(AcademicYearContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAcademicYearContextOptional(): AcademicYearContextValue | null {
  return useContext(AcademicYearContext);
}
